import React, {useCallback, useEffect, useRef, useState} from "react";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import copyIcon from '../../../theme/assets/svg/tc_cab_copy_icon.svg';

import {getDashboard, getReferrals, getUserData, setReferrer} from "../../../store/cabinet/action";

const CabinetMainReferral = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const dashboard = useSelector(store => store.cabinet.dashboard);
    // can_set_referrer приходит только из GET /user/referrals — в дашборде его нет
    const referralProgram = useSelector(store => store.cabinet.referrals);
    const userData = useSelector(store => store.cabinet.userData);
    const setReferrerStarted = useSelector(store => store.cabinet.setReferrerStarted);
    const setReferrerErr = useSelector(store => store.cabinet.setReferrerErr);

    // какое поле только что скопировали — подпись держится пару секунд
    const [copiedField, setCopiedField] = useState(false);
    const [inviterCode, setInviterCode] = useState('');
    const timerRef = useRef(false);

    const referrals = dashboard?.referrals;
    const referralCode = referrals?.referral_code || '';
    const referralLink = referralCode ? `${window.location.origin}/sign-up/${referralCode}` : '';

    // Указать пригласителя можно один раз и только пока его нет, а сам
    // пользователь ещё никого не привёл. Решает сервер — мы лишь показываем поле
    const canSetReferrer = !!referralProgram?.can_set_referrer;
    // Два разных «нельзя»: пригласитель уже есть — говорим об этом; иначе поле
    // просто не показываем (у пользователя уже свои рефералы, менять нечего)
    const hasReferrer = !!userData?.user?.has_referrer;

    useEffect(()=>{
        if (!referralProgram){
            dispatch(getReferrals());
        }
    },[referralProgram, dispatch])

    useEffect(()=>{
        return ()=>{
            if (timerRef.current){
                clearTimeout(timerRef.current);
            }
        };
    },[])

    const inviterHandler = () =>{
        const code = inviterCode.trim();
        if (!code || setReferrerStarted){
            return;
        }

        dispatch(setReferrer(code, ()=>{
            setInviterCode('');
            // пригласитель влияет на флаги в /user, на /user/referrals и на счётчики
            dispatch(getReferrals());
            dispatch(getDashboard());
            dispatch(getUserData());
        }));
    };

    // 422 errors.referral_code — нет такого кода, свой собственный,
    // пригласитель уже указан или у пользователя уже есть рефералы
    const inviterErr = setReferrerErr?.errors?.referral_code?.[0]
        || setReferrerErr?.message
        || '';

    const copyHandler = useCallback((field, value)=>{
        if (!value || !navigator?.clipboard){
            return;
        }

        navigator.clipboard.writeText(value).then(()=>{
            setCopiedField(field);

            if (timerRef.current){
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(()=>{ setCopiedField(false); }, 2000);
        }).catch(()=>{});
    },[])

    const stats = [
        {key: 'total', name: t('Всего рефералов'), value: referrals?.total || 0},
        {key: 'active', name: t('Активных рефералов'), value: referrals?.active || 0},
        {key: 'earned', name: `${t('Заработано')} $TC`, value: referrals?.earned_total || 0}
    ];

    return (
        <section className={`${Styles.container}`}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetPC}`}>
                <div className={`${Styles.cardWrap} ${Styles.border3dOuterXl}`}>
                    <div className={`${Styles.cardTitle}`}>{t('Реферальная программа')}</div>

                    <div className={`${Styles.fieldsWrap} ${(canSetReferrer || hasReferrer) ? Styles.fieldsWrapWide : ''}`}>
                        <div className={`${Styles.fieldBox}`}>
                            <div className={`${Styles.fieldName}`}>{t('Реферальная ссылка')}</div>
                            <div
                                className={`${Styles.fieldValue}`}
                                onClick={()=>{copyHandler('link', referralLink)}}
                            >
                                <span>{copiedField === 'link' ? t('Скопировано') : referralLink}</span>
                                <img src={copyIcon} alt={t('Скопировать')}/>
                            </div>
                        </div>

                        <div className={`${Styles.fieldBox} ${Styles.fieldBoxCode}`}>
                            <div className={`${Styles.fieldName}`}>{t('Реферальный код')}</div>
                            <div
                                className={`${Styles.fieldValue}`}
                                onClick={()=>{copyHandler('code', referralCode)}}
                            >
                                <span>{copiedField === 'code' ? t('Скопировано') : referralCode}</span>
                                <img src={copyIcon} alt={t('Скопировать')}/>
                            </div>
                        </div>

                        {!canSetReferrer && hasReferrer ? (
                            <div className={`${Styles.fieldBox} ${Styles.fieldBoxCode}`}>
                                <div className={`${Styles.fieldName}`}>{t('Код пригласителя')}</div>
                                <div className={`${Styles.inviterDone}`}>{t('Пригласитель указан')}</div>
                            </div>
                        ) : null}

                        {canSetReferrer ? (
                            <div className={`${Styles.fieldBox} ${Styles.fieldBoxCode}`}>
                                <div className={`${Styles.fieldName}`}>{t('Код пригласителя')}</div>
                                <div className={`${Styles.inviterRow}`}>
                                    <input
                                        className={`${Styles.inviterInput}`}
                                        type="text"
                                        value={inviterCode}
                                        placeholder={t('Введите код')}
                                        maxLength={32}
                                        disabled={setReferrerStarted}
                                        onChange={(e)=>{setInviterCode(e.target.value)}}
                                        onKeyDown={(e)=>{ if (e.key === 'Enter'){ inviterHandler(); } }}
                                    />
                                    <div
                                        className={`${Styles.inviterBtn} ${(!inviterCode.trim() || setReferrerStarted) ? Styles.inviterBtnDsb : ''}`}
                                        onClick={inviterHandler}
                                    >
                                        {setReferrerStarted ? '…' : '✓'}
                                    </div>
                                </div>
                                {inviterErr ? <div className={`${Styles.inviterErr}`}>{inviterErr}</div> : null}
                            </div>
                        ) : null}
                    </div>

                    <div className={`${Styles.statWrap}`}>
                        {stats.map((stat, index)=>(
                            <React.Fragment key={'cabinet-referral-stat_' + stat.key}>
                                {index ? <div className={`${Styles.statDivider}`}/> : null}
                                <div className={`${Styles.statItem}`}>
                                    <div className={`${Styles.statName}`}>{stat.name}</div>
                                    <div className={`${Styles.statValue}`}>{stat.value}</div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    <div
                        className={`${Styles.cardBtn}`}
                        onClick={()=>{navigate('/cabinet/apostol')}}
                    >
                        {t('Подробнее')}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CabinetMainReferral;
